import { memo, useCallback, useEffect, useRef, useState } from "react";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { ClickableQrCode } from "@/components/ui/ClickableQrCode";
import { Dialog } from "@/components/ui/Dialog";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  createProjectFotoSession,
  getProjectFotoMobileUploadQr,
  listProjectFotoFolders,
  listProjectFotos,
  openProjectFotoSession,
  renameProjectFoto,
  resolveProjectFotoPreviewUrl,
  uploadProjectFotoFile,
  type ProjectFoto,
  type ProjectFotoFolder,
  type ProjectFotoSession,
} from "@/lib/projectOverview";
import { toast } from "@/lib/toast";
import {
  ProjectFotoLightbox,
  projectFotoLightboxState,
  type ProjectFotoLightboxState,
} from "@/projectOverview/ProjectFotoLightbox";
import { PROJECT_OVERVIEW_OVERLAY_Z } from "@/projectOverview/projectOverviewLayers";

const POLL_MS = 4000;

function filenameStem(name: string): string {
  const idx = name.lastIndexOf(".");
  return idx > 0 ? name.slice(0, idx) : name;
}

function applyFilenameStem(filename: string, stem: string): string {
  const idx = filename.lastIndexOf(".");
  const ext = idx > 0 ? filename.slice(idx) : "";
  return `${stem}${ext}`;
}

function countPhotos(sessions: ProjectFotoSession[]): number {
  return sessions.reduce((sum, s) => sum + (s.photos?.length ?? 0), 0);
}

function patchFotoInSessions(
  sessions: ProjectFotoSession[],
  fotoId: number,
  patch: Partial<ProjectFoto>,
): ProjectFotoSession[] {
  return sessions.map((session) => ({
    ...session,
    photos: (session.photos || []).map((p) =>
      p.id === fotoId ? { ...p, ...patch } : p,
    ),
  }));
}

export function ProjectFotosDialog({
  open,
  orderId,
  onClose,
  onChanged,
  initialUploadMode = false,
}: {
  open: boolean;
  orderId: number;
  onClose: () => void;
  onChanged: () => void;
  initialUploadMode?: boolean;
}) {
  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessions, setSessions] = useState<ProjectFotoSession[]>([]);
  const [folders, setFolders] = useState<ProjectFotoFolder[]>([]);
  const [uploadMode, setUploadMode] = useState(false);
  const [folderMode, setFolderMode] = useState<"new" | "existing">("new");
  const [title, setTitle] = useState("");
  const [selectedFolderId, setSelectedFolderId] = useState("");
  const [activeSession, setActiveSession] = useState<ProjectFotoSession | null>(null);
  const [creating, setCreating] = useState(false);
  const [qrUri, setQrUri] = useState<string | null>(null);
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState<ProjectFotoLightboxState | null>(null);

  const openFotoLightbox = useCallback(
    (foto: ProjectFoto) => {
      const fromSessions = sessions.find((session) =>
        (session.photos || []).some((photo) => photo.id === foto.id),
      );
      const session =
        fromSessions ??
        (activeSession && (activeSession.photos || []).some((photo) => photo.id === foto.id)
          ? activeSession
          : null);
      setLightbox(projectFotoLightboxState(orderId, session?.photos || [], foto));
    },
    [activeSession, orderId, sessions],
  );
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const photoCountRef = useRef(0);

  const stopPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const applySessions = useCallback(
    (list: ProjectFotoSession[], opts?: { notifyParent?: boolean }) => {
      const newCount = countPhotos(list);
      const prevCount = photoCountRef.current;
      photoCountRef.current = newCount;
      setSessions(list);
      setActiveSession((prev) => {
        if (!prev) return null;
        const updated = list.find((s) => s.id === prev.id);
        return updated ?? prev;
      });
      if (opts?.notifyParent && newCount !== prevCount) {
        onChanged();
      }
    },
    [onChanged],
  );

  const refreshSessions = useCallback(
    async (opts?: { silent?: boolean; notifyParent?: boolean }) => {
      const silent = opts?.silent ?? false;
      if (!silent) setInitialLoading(true);
      setError(null);
      try {
        const list = await listProjectFotos(orderId);
        applySessions(list, { notifyParent: opts?.notifyParent ?? !silent });
      } catch (err) {
        if (!silent) {
          const message =
            err instanceof ApiError
              ? err.message
              : err instanceof Error
                ? err.message
                : "Fotos konnten nicht geladen werden.";
          setError(message);
        }
      } finally {
        if (!silent) setInitialLoading(false);
      }
    },
    [orderId, applySessions],
  );

  const loadFolders = useCallback(async () => {
    try {
      const list = await listProjectFotoFolders(orderId);
      setFolders(list);
      if (list.length > 0 && !selectedFolderId) {
        setSelectedFolderId(list[0]!.sharepoint_folder_id);
      }
    } catch {
      setFolders([]);
    }
  }, [orderId, selectedFolderId]);

  useEffect(() => {
    if (!open) {
      stopPoll();
      setUploadMode(false);
      setActiveSession(null);
      setTitle("");
      setQrUri(null);
      setQrUrl(null);
      setFolderMode("new");
      setSelectedFolderId("");
      photoCountRef.current = 0;
      return;
    }
    setUploadMode(initialUploadMode);
    void refreshSessions();
    void loadFolders();
  }, [open, orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || !activeSession) {
      stopPoll();
      return;
    }
    stopPoll();
    pollRef.current = setInterval(() => {
      void refreshSessions({ silent: true, notifyParent: true });
    }, POLL_MS);
    return () => stopPoll();
  }, [open, activeSession?.id, refreshSessions, stopPoll]);

  async function activateSession(session: ProjectFotoSession) {
    const qr = await getProjectFotoMobileUploadQr(orderId, session.id);
    setActiveSession(session);
    setQrUri(qr.qr_data_uri || null);
    setQrUrl(qr.url || null);
    setUploadMode(true);
  }

  async function startUploadSession() {
    setCreating(true);
    setError(null);
    try {
      let session: ProjectFotoSession;
      if (folderMode === "existing") {
        const folder = folders.find((f) => f.sharepoint_folder_id === selectedFolderId);
        if (!folder) {
          toast.error("Bitte einen bestehenden Ordner auswählen.");
          return;
        }
        session = await openProjectFotoSession(orderId, {
          sharepoint_folder_id: folder.sharepoint_folder_id,
          folder_segment: folder.folder_segment,
          title: folder.title,
        });
      } else {
        const trimmed = title.trim();
        if (!trimmed) {
          toast.error("Bitte einen Titel für den Foto-Ordner eingeben.");
          return;
        }
        session = await createProjectFotoSession(orderId, trimmed);
      }
      await activateSession(session);
      await refreshSessions({ silent: true });
      await loadFolders();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Upload-Session konnte nicht erstellt werden.";
      toast.error(message);
    } finally {
      setCreating(false);
    }
  }

  async function handleDesktopFiles(files: FileList | null) {
    if (!files?.length || !activeSession) return;
    setUploading(true);
    try {
      const uploaded: ProjectFoto[] = [];
      for (const file of Array.from(files)) {
        uploaded.push(await uploadProjectFotoFile(orderId, activeSession.id, file));
      }
      setSessions((prev) =>
        prev.map((s) =>
          s.id === activeSession.id
            ? { ...s, photos: [...(s.photos || []), ...uploaded] }
            : s,
        ),
      );
      setActiveSession((prev) =>
        prev
          ? { ...prev, photos: [...(prev.photos || []), ...uploaded] }
          : prev,
      );
      photoCountRef.current += uploaded.length;
      onChanged();
      toast.success("Foto(s) hochgeladen.");
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Upload fehlgeschlagen.";
      toast.error(message);
      void refreshSessions({ silent: true });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  const handleRename = useCallback(
    async (foto: ProjectFoto, newStem: string) => {
      const stem = newStem.trim();
      if (!stem) return;
      const newFilename = applyFilenameStem(foto.filename, stem);
      const oldFilename = foto.filename;
      setSessions((prev) =>
        patchFotoInSessions(prev, foto.id, { filename: newFilename }),
      );
      setActiveSession((prev) =>
        prev
          ? {
              ...prev,
              photos: (prev.photos || []).map((p) =>
                p.id === foto.id ? { ...p, filename: newFilename } : p,
              ),
            }
          : prev,
      );
      try {
        const updated = await renameProjectFoto(orderId, foto.id, stem);
        setSessions((prev) => patchFotoInSessions(prev, foto.id, updated));
      } catch (err) {
        setSessions((prev) =>
          patchFotoInSessions(prev, foto.id, { filename: oldFilename }),
        );
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Umbenennen fehlgeschlagen.";
        toast.error(message);
      }
    },
    [orderId],
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        title="Projektfotos"
        description="Fotos werden auf SharePoint unter 08 - Fotos/YYYY-MM-DD-Titel abgelegt."
        size="2xl"
        fullScreenMobile
        zIndexClass={PROJECT_OVERVIEW_OVERLAY_Z}
        footer={
          <div className="flex justify-end gap-2">
            {uploadMode ? (
              <Button type="button" variant="secondary" onClick={() => setUploadMode(false)}>
                Zurück zur Übersicht
              </Button>
            ) : null}
            <Button type="button" variant="secondary" onClick={onClose}>
              Schließen
            </Button>
          </div>
        }
      >
        {error ? (
          <Alert variant="error" className="mb-4">
            {error}
          </Alert>
        ) : null}

        {initialLoading && sessions.length === 0 ? (
          <div className="flex items-center justify-center gap-2 py-10">
            <Spinner />
            <span className="text-sm text-label-secondary">Lade Fotos …</span>
          </div>
        ) : null}

        {!uploadMode ? (
          <SessionOverview
            sessions={sessions}
            orderId={orderId}
            loading={initialLoading}
            onStartUpload={() => setUploadMode(true)}
            onPreview={openFotoLightbox}
            onRename={handleRename}
          />
        ) : !activeSession ? (
          <div className="space-y-4 rounded-ios-lg border border-separator bg-card-elevated p-4">
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant={folderMode === "new" ? "primary" : "secondary"}
                onClick={() => setFolderMode("new")}
              >
                Neuer Ordner
              </Button>
              <Button
                type="button"
                size="sm"
                variant={folderMode === "existing" ? "primary" : "secondary"}
                onClick={() => setFolderMode("existing")}
                disabled={folders.length === 0}
              >
                Bestehender Ordner
              </Button>
            </div>

            {folderMode === "existing" ? (
              <div className="space-y-2">
                <label className="eh-label" htmlFor="foto-folder-select">
                  Ordner unter 08 - Fotos/
                </label>
                <select
                  id="foto-folder-select"
                  className="eh-input"
                  value={selectedFolderId}
                  onChange={(e) => setSelectedFolderId(e.target.value)}
                >
                  {folders.map((f) => (
                    <option key={f.sharepoint_folder_id} value={f.sharepoint_folder_id}>
                      {f.folder_segment}
                      {f.photo_count ? ` (${f.photo_count} Dateien)` : ""}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="space-y-2">
                <label className="eh-label" htmlFor="foto-title">
                  Titel für den Foto-Ordner
                </label>
                <input
                  id="foto-title"
                  className="eh-input"
                  placeholder="z. B. Baustelle Nord, Abnahme Dach"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
                <p className="text-xs text-label-tertiary">
                  Neuer Unterordner: 08 - Fotos/{"{heute}"}-{"{Titel}"}
                </p>
              </div>
            )}

            <Button type="button" onClick={() => void startUploadSession()} disabled={creating}>
              {creating ? "Wird vorbereitet …" : "Upload starten"}
            </Button>
          </div>
        ) : (
          <ActiveUploadPanel
            orderId={orderId}
            session={activeSession}
            qrUri={qrUri}
            qrUrl={qrUrl}
            uploading={uploading}
            fileInputRef={fileInputRef}
            onDesktopFiles={handleDesktopFiles}
            onPreview={openFotoLightbox}
            onRename={handleRename}
          />
        )}
      </Dialog>

      <ProjectFotoLightbox
        state={lightbox}
        onClose={() => setLightbox(null)}
        onIndexChange={(index) =>
          setLightbox((prev) => (prev ? { ...prev, index } : prev))
        }
      />
    </>
  );
}

function SessionOverview({
  sessions,
  orderId,
  loading,
  onStartUpload,
  onPreview,
  onRename,
}: {
  sessions: ProjectFotoSession[];
  orderId: number;
  loading: boolean;
  onStartUpload: () => void;
  onPreview: (foto: ProjectFoto) => void;
  onRename: (foto: ProjectFoto, stem: string) => void;
}) {
  const total = countPhotos(sessions);
  return (
    <div className="space-y-4">
      <Button type="button" size="sm" onClick={onStartUpload}>
        + Fotos hochladen
      </Button>
      {total === 0 && !loading ? (
        <p className="text-sm text-label-secondary">Noch keine Fotos vorhanden.</p>
      ) : (
        <div className="max-h-[min(52vh,520px)] space-y-5 overflow-y-auto overscroll-contain pr-1">
          {sessions.map((session) => (
            <section key={session.id}>
              <h3 className="mb-1 text-sm font-semibold text-label">{session.title}</h3>
              <p className="mb-2 text-xs text-label-tertiary">
                Fotos/{session.folder_segment}
              </p>
              {session.photos?.length ? (
                <ul className="grid grid-cols-4 gap-1.5 sm:grid-cols-5 md:grid-cols-6">
                  {session.photos.map((foto) => (
                    <FotoCard
                      key={foto.id}
                      orderId={orderId}
                      foto={foto}
                      onPreview={() => onPreview(foto)}
                      onRename={onRename}
                    />
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-label-tertiary">Keine Dateien.</p>
              )}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function ActiveUploadPanel({
  orderId,
  session,
  qrUri,
  qrUrl,
  uploading,
  fileInputRef,
  onDesktopFiles,
  onPreview,
  onRename,
}: {
  orderId: number;
  session: ProjectFotoSession;
  qrUri: string | null;
  qrUrl: string | null;
  uploading: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onDesktopFiles: (files: FileList | null) => void;
  onPreview: (foto: ProjectFoto) => void;
  onRename: (foto: ProjectFoto, stem: string) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-ios-lg border border-separator bg-card-elevated p-3 text-sm">
        <p className="font-medium text-label">{session.title}</p>
        <p className="text-xs text-label-tertiary">SharePoint: 08 - Fotos/{session.folder_segment}</p>
      </div>

      {qrUri ? (
        <div className="hidden flex-col items-center gap-2 rounded-ios-lg border border-separator bg-fill-tertiary/60 px-4 py-4 lg:flex">
          <ClickableQrCode
            src={qrUri}
            href={qrUrl}
            alt="QR-Code für Handy-Upload"
            className="h-36 w-36 rounded-ios border border-separator bg-white p-2 shadow-sm"
          />
          <p className="text-center text-xs text-label-secondary">
            QR-Code mit dem Smartphone scannen
          </p>
          {qrUrl ? (
            <a
              href={qrUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="break-all text-center text-xs text-accent"
            >
              {qrUrl}
            </a>
          ) : null}
        </div>
      ) : null}

      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="w-full lg:w-auto"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
      >
        {uploading ? (
          "Wird hochgeladen …"
        ) : (
          <>
            <span className="lg:hidden">Aus Mediathek</span>
            <span className="hidden lg:inline">Am PC hochladen</span>
          </>
        )}
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        multiple
        hidden
        onChange={(e) => onDesktopFiles(e.target.files)}
      />

      {session.photos?.length ? (
        <ul className="grid max-h-48 grid-cols-4 gap-1.5 overflow-y-auto sm:grid-cols-5">
          {session.photos.map((foto) => (
            <FotoCard
              key={foto.id}
              orderId={orderId}
              foto={foto}
              onPreview={() => onPreview(foto)}
              onRename={onRename}
            />
          ))}
        </ul>
      ) : (
        <p className="text-sm text-label-secondary">
          <span className="lg:hidden">Noch keine Fotos – aus der Mediathek auswählen.</span>
          <span className="hidden lg:inline">Noch keine Fotos – QR scannen oder am PC hochladen.</span>
        </p>
      )}
    </div>
  );
}

const FotoCard = memo(function FotoCard({
  orderId,
  foto,
  onPreview,
  onRename,
}: {
  orderId: number;
  foto: ProjectFoto;
  onPreview: () => void;
  onRename: (foto: ProjectFoto, stem: string) => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState("");
  const isImage = foto.is_image ?? (foto.content_type || "").startsWith("image/");
  const previewUrl = resolveProjectFotoPreviewUrl(orderId, foto);

  function startRename() {
    setDraft(filenameStem(foto.filename));
    setRenaming(true);
  }

  function cancelRename() {
    setRenaming(false);
    setDraft("");
  }

  function saveRename() {
    const stem = draft.trim();
    if (!stem) return;
    setRenaming(false);
    setDraft("");
    onRename(foto, stem);
  }

  return (
    <li className="overflow-hidden rounded-md border border-separator bg-card">
      {isImage ? (
        <button
          type="button"
          onClick={onPreview}
          className="block h-16 w-full overflow-hidden bg-fill-tertiary"
        >
          <img
            src={previewUrl}
            alt={foto.filename}
            loading="lazy"
            decoding="async"
            crossOrigin="use-credentials"
            className="h-full w-full object-cover"
          />
        </button>
      ) : (
        <a
          href={foto.sharepoint_web_url || previewUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block px-1 py-4 text-center text-[10px] text-label hover:bg-fill-tertiary"
        >
          {foto.filename}
        </a>
      )}

      <div className="border-t border-separator px-1 py-1">
        {renaming ? (
          <div className="flex gap-0.5">
            <input
              className="eh-input min-w-0 flex-1 px-1 py-0.5 text-[10px]"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveRename();
                if (e.key === "Escape") cancelRename();
              }}
              autoFocus
            />
            <button
              type="button"
              className="shrink-0 px-1 text-[10px] text-accent"
              onClick={saveRename}
            >
              OK
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-0.5">
            <span
              className="min-w-0 truncate text-[10px] text-label-secondary"
              title={foto.filename}
            >
              {foto.filename}
            </span>
            <button
              type="button"
              className="shrink-0 text-[10px] text-accent hover:underline"
              onClick={startRename}
            >
              ✎
            </button>
          </div>
        )}
      </div>
    </li>
  );
});
